"use client";
import { useCallback, useEffect, useState } from "react"
import { isNodeSelection } from "@tiptap/react";
import { useHotkeys } from "react-hotkeys-hook"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
} from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Icons ---
import { AiSparklesIcon } from "@/tiptap/icons/ai-sparkles-icon"

export const AI_ASK_SHORTCUT_KEY = "mod+j"
export const AI_EXTENSIONS = ["aiGeneration", "ai"]
export const EXCLUDED_SELECTION_TYPES = ["codeBlock", "image", "imageUpload"]

export const canPerformAiAsk = editor => {
  if (!editor || !editor.isEditable) return false
  // TODO: Wait until AI extensions support for image
  if (
    !isExtensionAvailable(editor, AI_EXTENSIONS) ||
    isNodeTypeSelected(editor, ["image", "horizontalRule", "tocNode"])
  )
    return false

  const { selection } = editor.state
  if (!selection || selection.empty) return false

  if (isNodeSelection(selection)) {
    const selectedNode = selection.node
    if (EXCLUDED_SELECTION_TYPES.includes(selectedNode.type.name)) {
      return false
    }
  }

  return true
}

export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isExtensionAvailable(editor, AI_EXTENSIONS)) return false

  if (!editor.isActive("code")) {
    return canPerformAiAsk(editor);
  }

  return true
}

/**
 * Custom hook that provides AI ask functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyAiButton() {
 *   const { isVisible, handleAiAsk } = useAiAsk()
 *   if (!isVisible) return null
 *   return <button onClick={handleAiAsk}>Ask AI</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedAiButton() {
 *   const { isVisible, handleAiAsk, label } = useAiAsk({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onAiAsked: () => console.log('AI triggered!')
 *   })
 *
 *   return isVisible ? (
 *     <button onClick={handleAiAsk} aria-label={label}>
 *       Ask AI Assistant
 *     </button>
 *   ) : null
 * }
 * ```
 */
export function useAiAsk(config = {}) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onAiAsked,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canAiAsk = canPerformAiAsk(editor)

  const handleAiAsk = useCallback(() => {
    if (!editor || !canAiAsk) return false

    const success = editor.chain().focus().aiGenerationShow().run()
    if (success) {
      onAiAsked?.()
    }
    return success
  }, [canAiAsk, editor, onAiAsked])

  useEffect(() => {
    if (!editor) {
      setIsVisible(false)
      return
    }

    const updateVisibility = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    updateVisibility()

    editor.on("selectionUpdate", updateVisibility)

    return () => {
      editor.off("selectionUpdate", updateVisibility)
    };
  }, [editor, hideWhenUnavailable])

  useHotkeys(AI_ASK_SHORTCUT_KEY, (event) => {
    event.preventDefault()
    handleAiAsk()
  }, {
    enabled: isVisible && canAiAsk,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  return {
    isVisible,
    handleAiAsk,
    canAiAsk,
    label: "Ask AI Assistant",
    shortcutKeys: AI_ASK_SHORTCUT_KEY,
    Icon: AiSparklesIcon,
  }
}
